import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  AssemblyPrintSource,
  DocumentCarouselContext,
  IActionCriteria,
  IDocumentPrint,
} from '../../components/document/DocumentProvider';
import { useQuery } from '@apollo/client';
import {
  IAssemblyDocumentCarouselQueryResult,
  IAssemblyDocumentCarouselQueryVariables,
  IAssemblyPickingInstruction,
  IAssemblyPickingInstructionCarouselQueryResult,
  IAssemblyPickingInstructionCarouselQueryVariables,
  IAssemblyPickingInstructionGenerateRangeFromSalesOrderMutationResult,
  IAssemblyPickingInstructionGenerateRangeFromSalesOrderMutationVariables,
  IAssemblyPickingInstructionGenerateRangeMutationResult,
  IAssemblyPickingInstructionGenerateRangeMutationVariables,
  IAssemblyPickingInstructionReprintMutationResult,
  IAssemblyPickingInstructionReprintMutationVariables,
  ISalesOrder,
  ISalesOrderDocumentCarouselQueryResult,
  ISalesOrderDocumentCarouselQueryVariables,
  ReportType,
} from '../../graphql/schema/p4types';
import AssemblyPickingInstruction from '../../graphql/entities/product/AssemblyPickingInstruction';
import {
  getDocumentCarouselReportParameters,
  IDocumentValues,
  useActionFilters,
  useDocumentCarouselDefaults,
} from '../../components/document/documentCarouselUtil';
import DocumentCarouselHeader from '../../components/document/DocumentCarouselHeader';
import { FormProvider, useForm } from 'react-hook-form';
import DocumentCarouselTemplate from '../../components/document/DocumentCarouselTemplate';
import DocumentCarouselOutput from '../../components/document/DocumentCarouselOutput';
import DocumentCarouselButtons from '../../components/document/DocumentCarouselButtons';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useDocumentCarouselComplete, useDocumentJobCompletion } from '../../utils/DocumentUtils';
import getClientMutationId from '../../utils/getClientMutationId';
import DocumentCarouselEmail from '../../components/document/DocumentCarouselEmail';
import SalesOrderDocumentCarouselQuery from '../../graphql/queries/document/SalesOrderDocumentCarouselQuery.graphql';
import AssemblyDocumentCarouselQuery from '../../graphql/queries/document/AssemblyDocumentCarouselCardQuery.graphql';
import AssemblyPickingInstructionCarouselQuery from '../../graphql/queries/document/AssemblyPickingInstructionDocumentCarouselQuery.graphql';
import DocumentCarouselPrintOptions from '../../components/document/DocumentCarouselPrintOptions';
import DocumentCarouselSummary from '../../components/document/DocumentCarouselSummary';
import { useCookies } from 'react-cookie';
import { DocumentCarouselHeaderInfo } from '../../components/document/DocumentCarouselHeaderInfo';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import DocumentCarouselSkeleton from '../../components/document/DocumentCarouselSkeleton';
import { DocumentCarouselPickingInstructionConfiguration } from '../../actions/sales/DocumentCarouselPickingInstructionConfiguration';
import AssemblyPickingInstructionFromSalesOrderRangeMutation from '../../graphql/mutations/product/AssemblyPickingInstructionGenerateFromSalesOrderRangeMutation.graphql';
import AssemblyPickingInstructionRangeMutation from '../../graphql/mutations/product/AssemblyPickingInstructionGenerateRangeMutation.graphql';
import AssemblyPickingInstructionReprintMutation from '../../graphql/mutations/product/AssemblyPickingInstructionReprintMutation.graphql';

//Note - DocumentModal2 currently uses 'documentSummary' instead of 'DOCUMENT_SUMMARY' - Possible cause for minor friction in transition.
export const DOCUMENT_SUMMARY = 'DOCUMENT_SUMMARY';
export const PRINT_INDIVIDUAL_PREFERENCE_COOKIE = 'PRINT_INDIVIDUAL_PREFERENCE_COOKIE';

/**
 * Render the content for the Picking Instruction Document Carousel.
 * All querying should be completed before rendering this element so that 'documentDefaults' are accurate/up-to-date when the user see's them.
 * @param documentDefaults - Default document values. Should be final on initial load.
 * @param actionCriteria - The action criteria driving this particular print.
 * @param count - How many records are in this print job.
 * @param singleRecipient - Flag to indicate if all records are a single Customer/Supplier.
 * @param singlePrint - Flag to indicate if we're printing a single record.
 * @param singlePrintNode - If we're printing a single record, this should be the document-queried subset of that record for display/defaulting.
 *                        - Note - Will still be populated for non-single, will be arbitrarily the first record matching the criteria.
 * @param setCookie - Pass-through of cookie setter, used to update 'showSummary' and 'printContiguous' cookie values.
 * @param documentPrint - Print action object - Provides additional detail such as whether this is a reprint, print completion callback or additional email configuration.
 */
const AssemblyPickingInstructionDocumentContent = ({
  documentDefaults,
  actionCriteria,
  count,
  singleRecipient,
  singlePrint,
  singlePrintNode,
  setCookie,
  documentPrint,
}: {
  documentDefaults: IDocumentValues;
  actionCriteria: IActionCriteria;
  count: number;
  singleRecipient: boolean;
  singlePrint: boolean;
  singlePrintNode: ISalesOrder | IAssembly | IAssemblyPickingInstruction;
  setCookie: ReturnType<typeof useCookies>[1];
  documentPrint: IDocumentPrint;
}) => {
  const { onSkip } = useContext(DocumentCarouselContext);
  const { reprint, onPrintComplete, metaData } = documentPrint;
  const [reset, setReset] = useState<boolean>(false);

  //Use react-hook-form to manage our form values for better typing and performance than ConnectedForm.
  const methods = useForm<IDocumentValues>({
    defaultValues: documentDefaults,
  });
  const { getValues, watch } = methods;

  const onDocumentComplete = useDocumentCarouselComplete({
    watch,
    entity: AssemblyPickingInstruction,
  });

  const onJobStart = useCallback(() => {
    onSkip(true);
  }, [onSkip]);

  const onJobComplete = useDocumentJobCompletion({
    evictionFieldNames: ['salesOrder'],
    onDocumentComplete,
    onPrintComplete,
  });

  const [printPickingInstructionSalesOrderRange] = useBackgroundMutation<
    IAssemblyPickingInstructionGenerateRangeFromSalesOrderMutationResult,
    IAssemblyPickingInstructionGenerateRangeFromSalesOrderMutationVariables
  >(AssemblyPickingInstructionFromSalesOrderRangeMutation!, {
    onStartJob: onJobStart,
    onCompleteJob: onJobComplete,
  });

  const [printPickingInstructionRange] = useBackgroundMutation<
    IAssemblyPickingInstructionGenerateRangeMutationResult,
    IAssemblyPickingInstructionGenerateRangeMutationVariables
  >(AssemblyPickingInstructionRangeMutation!, {
    onStartJob: onJobStart,
    onCompleteJob: onJobComplete,
  });

  const [reprintPickingInstructionRange] = useBackgroundMutation<
    IAssemblyPickingInstructionReprintMutationResult,
    IAssemblyPickingInstructionReprintMutationVariables
  >(AssemblyPickingInstructionReprintMutation!, {
    onStartJob: onJobStart,
    onCompleteJob: onJobComplete,
  });

  const onPrintClick = useCallback(() => {
    const reportParameters = getDocumentCarouselReportParameters({ getValues, reprint });

    if (reprint) {
      reprintPickingInstructionRange({
        variables: {
          input: {
            filters: actionCriteria.filters,
            pickingInstructions: normalizeVariables(actionCriteria.actionEntities),
            include: actionCriteria.include,
            clientMutationId: getClientMutationId(),
            reportParameters: reportParameters,
          },
        },
      });
    } else if (metaData?.source == AssemblyPrintSource.Assembly) {
      printPickingInstructionRange({
        variables: {
          input: {
            filters: actionCriteria.filters,
            assemblies: normalizeVariables(actionCriteria.actionEntities),
            include: actionCriteria.include,
            clientMutationId: getClientMutationId(),
            reportParameters: reportParameters,
            reset,
          },
        },
      });
    } else if (metaData?.source == AssemblyPrintSource.SalesOrder) {
      printPickingInstructionSalesOrderRange({
        variables: {
          input: {
            filters: actionCriteria.filters,
            salesOrders: normalizeVariables(actionCriteria.actionEntities),
            include: actionCriteria.include,
            clientMutationId: getClientMutationId(),
            reportParameters: reportParameters,
            reset,
          },
        },
      });
    }
  }, [
    actionCriteria.actionEntities,
    actionCriteria.filters,
    actionCriteria.include,
    getValues,
    printPickingInstructionRange,
    reprint,
    reprintPickingInstructionRange,
    printPickingInstructionSalesOrderRange,
    reset,
    metaData?.source,
  ]);

  const additionalRecipient = singlePrintNode;

  const contentLeft = useMemo<string>(() => {
    if (!singlePrint) {
      return 'Assembly Picking Instructions';
    }

    const orderNumber =
      singlePrintNode?.assembly?.orderNumber ||
      singlePrintNode?.salesOrder?.orderNumber ||
      singlePrintNode?.orderNumber;

    return metaData?.source === AssemblyPrintSource.Assembly ? `Assembly ${orderNumber}` : `Sales Order ${orderNumber}`;
  }, [singlePrint, singlePrintNode, metaData?.source]);

  const contentRight = useMemo<string>(() => {
    if (!singlePrint || metaData?.source == AssemblyPrintSource.Assembly) {
      return '';
    }

    if (metaData?.source == AssemblyPrintSource.SalesOrder) {
      return `Customer ${
        (singlePrintNode?.salesOrder?.customer?.name || singlePrintNode?.customer?.name) ?? 'Unknown'
      }`;
    }
  }, [singlePrint, singlePrintNode, metaData?.source]);

  return (
    <FormProvider {...methods}>
      <DocumentCarouselHeader
        entity={AssemblyPickingInstruction}
        count={count}
        secondaryTitle={<DocumentCarouselHeaderInfo contentLeft={contentLeft} contentRight={contentRight} />}
        reprint={reprint}
      />
      <div style={{ padding: '1em' }}>
        <DocumentCarouselTemplate
          noDefault
          reportType={ReportType.AssemblyPickingList}
          singleRecipient={singleRecipient}
        />
        <DocumentCarouselOutput noDefault singleRecipient={singleRecipient} />
        <DocumentCarouselSummary singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselPrintOptions singlePrint={singlePrint} setCookie={setCookie} />
        <DocumentCarouselEmail
          entity={AssemblyPickingInstruction}
          recipient={documentDefaults?.recipientForEmail ?? singlePrintNode?.customer}
          singleRecipient={singleRecipient}
          emailOptions={{
            additionalRecipients: additionalRecipient ? [additionalRecipient] : [],
          }}
        />
      </div>
      <DocumentCarouselPickingInstructionConfiguration reset={reset} onResetToggle={setReset} />
      <DocumentCarouselButtons onPrint={onPrintClick} />
    </FormProvider>
  );
};

/**
 * @param actionCriteria - The action criteria driving this particular print.
 * @param reprint - This is a reprint operation, will will use a different mutation and data queries.
 */
export const AssemblyPickingInstructionDocumentCarouselCard = ({
  actionCriteria,
  documentPrint,
}: {
  actionCriteria: IActionCriteria;
  documentPrint: IDocumentPrint;
}) => {
  const { reprint, metaData } = documentPrint;

  const filters = useActionFilters(actionCriteria);
  const { data: salesOrderData, loading: salesOrderLoading } = useQuery<
    ISalesOrderDocumentCarouselQueryResult,
    ISalesOrderDocumentCarouselQueryVariables
  >(SalesOrderDocumentCarouselQuery, {
    variables: {
      filters: filters,
      first: 2,
      skip: reprint || metaData?.source == AssemblyPrintSource.Assembly,
    },
  });

  const { data: assemblyData, loading: assembliesLoading } = useQuery<
    IAssemblyDocumentCarouselQueryResult,
    IAssemblyDocumentCarouselQueryVariables
  >(AssemblyDocumentCarouselQuery, {
    variables: {
      filters: filters,
      first: 2,
      skip: reprint || metaData?.source == AssemblyPrintSource.SalesOrder,
    },
  });

  const { data: reprintData, loading: reprintLoading } = useQuery<
    IAssemblyPickingInstructionCarouselQueryResult,
    IAssemblyPickingInstructionCarouselQueryVariables
  >(AssemblyPickingInstructionCarouselQuery, {
    variables: {
      filters: filters,
      first: 2,
      aggregateCriteria: {
        groupColumns: ['salesOrder.customer.id'],
        sumColumns: ['id'],
      },
      skip: !reprint,
    },
  });

  const data = useMemo(() => {
    if (reprint) {
      return reprintData;
    } else if (metaData?.source == AssemblyPrintSource.Assembly) {
      return assemblyData;
    } else if (metaData?.source == AssemblyPrintSource.SalesOrder) {
      return salesOrderData;
    }
  }, [reprint, metaData?.source, reprintData, assemblyData, salesOrderData]);
  const loading = salesOrderLoading || assembliesLoading || reprintLoading;

  const singlePrint: boolean = actionCriteria.include && actionCriteria.actionEntities.length === 1;
  //If the grouping gives a single Customer in the result, we can pull the document defaults off the Customer record and use it for the whole print.
  //(If there's >1 Customer, we cannot do this).
  const singleRecipient = true;
  const singlePrintNode = data?.listData?.edges?.[0]?.node as Maybe<ISalesOrder | IAssemblyPickingInstruction>;

  const [cookies, setCookie] = useCookies([DOCUMENT_SUMMARY, PRINT_INDIVIDUAL_PREFERENCE_COOKIE]);
  const documentDefaults = useDocumentCarouselDefaults({
    entity: AssemblyPickingInstruction,
    singleRecipient,
    recipient: singlePrintNode?.customer,
    cookies,
    allowEntityEmail: false,
  });

  if (data && !loading) {
    return (
      <AssemblyPickingInstructionDocumentContent
        documentDefaults={documentDefaults}
        setCookie={setCookie}
        actionCriteria={actionCriteria}
        count={data?.listData?.count ?? 0}
        singleRecipient={singleRecipient}
        singlePrint={singlePrint}
        singlePrintNode={singlePrintNode}
        documentPrint={documentPrint}
      />
    );
  }

  return <DocumentCarouselSkeleton />;
};
